import * as React from 'react';
import styled from 'styled-components';
import useAuth from '@sdk/authentication/useAuth';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext, { ngettext } from '@sdk/lang/gettext';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import TextField from '@sdk/components/forms/TextField';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';

import { PremiumGuardPassedConfig } from '../../../premiumGuard/PremiumGuard';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { FIELD_WIDTH_205 } from '../../constants';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { TabType } from '../../types';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_205}px;
`;

type Props = {
  premiumGuard: PremiumGuardPassedConfig;
};

const Attendance: React.FC<React.PropsWithChildren<Props>> = ({ premiumGuard }) => {
  const { toggleError, isFieldValid } = useSettingsErrorsContext();
  const { currentUser } = useAuth();
  const { userOrgSettings, onUserOrgSettingsChange, initialUserOrgSettings } = useUserOrgSettings();

  const validLateArrivalMinutes = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.integer]: true,
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
    });
  };

  const onLateArrivalChange = (value: boolean): void => {
    if (!isFieldValid('lateArrivalMinutes')) {
      onUserOrgSettingsChange({
        lateArrivalEnabled: value,
        lateArrivalMinutes: initialUserOrgSettings.lateArrivalMinutes,
      });

      toggleError(TabType.timeClock, 'lateArrivalMinutes', false);
    } else {
      onUserOrgSettingsChange({ lateArrivalEnabled: value });
    }
  };

  return (
    <>
      <CollapsibleSettingsSection
        isActive={userOrgSettings.lateArrivalEnabled}
        headerItem={
          <SettingsSwitcherRow
            disabled={!currentUser.isAdmin}
            pBottom={20}
            value={userOrgSettings.lateArrivalEnabled}
            onChange={(value): void => {
              onLateArrivalChange(value);
            }}
            title={gettext('Late arrival')}
            subtitle={gettext('Track when employees clock in later than scheduled')}
            premiumGuard={premiumGuard}
          />
        }
        contentItem={
          <Column pBottom={20}>
            <RowSpaceBetween>
              <>
                <Text14 themeColor={ThemeColorType.gray}>
                  <TranslateWithComponents
                    replaceComponents={{
                      component: (
                        <Text14 themeColor={ThemeColorType.primary}>
                          {userOrgSettings.lateArrivalMinutes || 0}
                        </Text14>
                      ),
                    }}>
                    {ngettext(
                      'When employees are [[component]] minute late to clock in',
                      'When employees are [[component]] minutes late to clock in',
                      Number(userOrgSettings.lateArrivalMinutes)
                    )}
                  </TranslateWithComponents>
                </Text14>
              </>

              <StyledTextField
                disabled={!currentUser.isAdmin}
                skipErrorMessage
                maxLength={10}
                value={userOrgSettings.lateArrivalMinutes}
                postfix={gettext('Minutes')}
                onChange={(e): void => {
                  onUserOrgSettingsChange({
                    lateArrivalMinutes: Number(e.currentTarget.value) || 0,
                  });
                  toggleError(
                    TabType.timeClock,
                    'lateArrivalMinutes',
                    !validLateArrivalMinutes(e.currentTarget.value)
                  );
                }}
                invalid={!validLateArrivalMinutes(userOrgSettings.lateArrivalMinutes)}
              />
            </RowSpaceBetween>
          </Column>
        }
      />
    </>
  );
};

export default Attendance;
